import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { useAnmeldung } from "../zustand/Anmeldung";
import { useDemo } from "../zustand/DemoZustand";
import useDokumentSitzung from "../haken/useDokumentSitzung";
import Folienbuehne from "../bausteine/Folienbuehne";
import Folienleiste from "../bausteine/Folienleiste";
import Vorfuehrung from "../bausteine/Vorfuehrung";
import Randnotizen from "../bausteine/Randnotizen";
import Notizfeld from "../bausteine/Notizfeld";
import { loeseVerankerung, verankere } from "../hilfen/dokumentmodell";
import { farbeFuer } from "../bausteine/Textwerk";
import { Kreuz, PfeilRechts, Warnung } from "../bausteine/Symbole";
import {
  baueFolie,
  baueKasten,
  folienVon,
  naechsteReihenfolge,
  SEITENVERHAELTNIS,
  sortierteFolien,
  stelleErsteFolieSicher,
  verschiebeFolie,
} from "../hilfen/folienmodell";
import useDateiZugang from "../haken/useDateiZugang";
import { benenneDokument, ladeDateiHoch } from "../dienst/server";

/*
  Eine Präsentation, offen.

  Links der Streifen mit allen Folien, in der Mitte die Bühne. Alles, was
  mit dem Netz zu tun hat, steckt in `useDokumentSitzung` — dieselbe
  Maschinerie wie beim Textdokument, denn eine Präsentation IST ein
  Dokument, nur mit anderem Inhalt.

  Rechts stehen die Kommentare der aktuellen Folie — derselbe Baustein wie
  am Textdokument. Über die Leiste geht es ins Vollbild und aufs Papier.
*/

export default function Praesentation({ dokumentId, oeffne }) {
  const { token, konto, fraktion } = useAnmeldung();
  const { zustand, ich, aktionen } = useDemo();
  const zugang = useDateiZugang();

  const {
    doc,
    satz,
    setzeSatz,
    laedt,
    fehler,
    setzeFehler,
    bereit,
    andere,
  } = useDokumentSitzung(dokumentId);

  const [titel, setzeTitel] = useState("");
  const [aktuelle, setzeAktuelle] = useState(0);
  const [gewaehlterKasten, setzeGewaehlterKasten] = useState(null);
  /* Zwingt ein Neuzeichnen, wenn sich am Foliengerüst etwas ändert. */
  const [takt, setzeTakt] = useState(0);

  /* Der Kasten, in dem zuletzt geschrieben wurde — an ihm hängt die Leiste. */
  const [schreibstelle, setzeSchreibstelle] = useState(null);
  const [rueckgaengig, setzeRueckgaengig] = useState(null);
  const [kannZurueck, setzeKannZurueck] = useState(false);
  const [kannVor, setzeKannVor] = useState(false);
  const [laedtBild, setzeLaedtBild] = useState(false);
  const [fuehrtVor, setzeFuehrtVor] = useState(false);
  const [druckt, setzeDruckt] = useState(false);
  /* Die ausgewählte Stelle im zuletzt benutzten Kasten — daraus wird ein Kommentar. */
  const [auswahl, setzeAuswahl] = useState(null);
  const [notizEntwurf, setzeNotizEntwurf] = useState(null);
  const [notizBearbeiten, setzeNotizBearbeiten] = useState(null);
  const bildWaehler = useRef(null);
  const rueckgaengigRef = useRef(null);
  const bindungenRef = useRef(new Set());
  /* Kastenkennung → Quill, damit „Zur Stelle" die Textstelle treffen kann. */
  const quillsRef = useRef(new Map());

  /*
    Die Textkästen melden ihre Bindung hier an.

    Warum nicht umgekehrt, warum bekommt der Kasten nicht einfach den
    Verwalter gereicht? Weil React die Wirkungen der Kinder VOR denen des
    Elternteils ausführt. Beim ersten Aufbau gäbe es den Verwalter zu dem
    Zeitpunkt noch gar nicht — die Bindung wäre nie angemeldet, und
    getippter Text ließe sich nicht zurücknehmen.

    Deshalb sammelt diese Liste die Bindungen, und der Verwalter holt sich
    beim Entstehen, was schon da ist.
  */
  const merkeBindung = useCallback((bindung, weg) => {
    if (weg) {
      bindungenRef.current.delete(bindung);
      if (rueckgaengigRef.current) rueckgaengigRef.current.trackedOrigins.delete(bindung);
      return;
    }
    bindungenRef.current.add(bindung);
    if (rueckgaengigRef.current) rueckgaengigRef.current.trackedOrigins.add(bindung);
  }, []);

  /*
    Meldet ein Kasten sich ab, verliert die Leiste ihn nur dann, wenn es
    auch der ist, an dem sie gerade hängt. Sonst risse ein Kasten, der beim
    Folienwechsel abgebaut wird, die Leiste eines anderen mit.

    `useCallback` ohne Abhängigkeiten ist hier Pflicht, keine Kosmetik: Die
    Funktion hängt im Aufbau-Vorgang jedes Textkastens. Entstünde sie bei
    jedem Zeichnen neu, würde jedes Quill samt Bindung an Yjs abgerissen
    und neu gebaut — bei jedem Tastendruck.
  */
  const merkeQuill = useCallback((kastenId, quill) => {
    if (quill) quillsRef.current.set(kastenId, quill);
    else quillsRef.current.delete(kastenId);
  }, []);

  const merkeSchreibstelle = useCallback((meldung) => {
    if (!meldung.quill) {
      setzeSchreibstelle((bisher) => (bisher === meldung.weg ? null : bisher));
      return;
    }

    setzeSchreibstelle(meldung.quill);

    /*
      Nur eine ECHTE Auswahl taugt für einen Kommentar. Eine Schreibmarke
      ohne Ausdehnung hätte nichts, woran der Kommentar hängen könnte —
      dann bleibt der Knopf gesperrt statt eine Notiz an ein Nichts zu
      heften.
    */
    const bereich = meldung.bereich;
    if (!bereich || bereich.length === 0) {
      setzeAuswahl(null);
      return;
    }
    setzeAuswahl({
      kasten: meldung.kasten,
      von: bereich.index,
      bis: bereich.index + bereich.length,
      zitat: meldung.quill.getText(bereich.index, bereich.length),
    });
  }, []);

  useEffect(() => {
    if (satz) setzeTitel(satz.titel);
  }, [satz]);

  /*
    Die erste Folie legt NUR an, wer die Präsentation angelegt hat — und
    erst, wenn die Sitzung bereit ist.

    Beides teuer gelernt: Vor „bereit" ginge die Folie nicht hinaus und
    bliebe im eigenen Browser. Und legten zwei Leute gleichzeitig an,
    stünde alles doppelt da.
  */
  useEffect(() => {
    if (!bereit || !satz) return;
    if (satz.konto !== konto.id) return;
    stelleErsteFolieSicher(doc);
  }, [bereit, satz, doc, konto]);

  /* Folien kommen und gehen — auch von den anderen. */
  useEffect(() => {
    if (!bereit) return undefined;
    const beiAenderung = () => setzeTakt((n) => n + 1);
    doc.on("update", beiAenderung);
    return () => doc.off("update", beiAenderung);
  }, [bereit, doc]);

  /*
    Rückgängig über die ganze Präsentation.

    ---------------------------------------------------------------
    Warum das nicht Quill übernimmt
    ---------------------------------------------------------------

    Quills eingebautes Rückgängig kennt nur seinen eigenen Textkasten. Es
    wüsste nichts von einem verschobenen Kasten, nichts von einer
    gelöschten Folie — und bei mehreren Kästen nähme jedes sein eigenes
    zurück, je nachdem, wo die Schreibmarke gerade steht.

    `Y.UndoManager` sitzt eine Ebene darunter, am Foliengerüst selbst, und
    nimmt deshalb alles zurück: Text, Maße, Folien.

    Entscheidend ist `trackedOrigins`. Zurückgenommen wird ausschließlich,
    was aus DIESEM Browser kam — eingetragen sind `null` (die eigenen
    Änderungen an Kästen und Folien) und die Bindungen der Textkästen, die
    sich beim Aufbauen selbst anmelden. Was über das Netz hereinkommt,
    trägt „server" und bleibt unberührt: Wer Strg+Z drückt, während ein
    anderer schreibt, nimmt seine eigene letzte Änderung zurück, nicht
    dessen Satz.
  */
  useEffect(() => {
    if (!bereit) return undefined;

    const verwalter = new Y.UndoManager(folienVon(doc), {
      trackedOrigins: new Set([null]),
      captureTimeout: 400,
    });
    /* Was sich schon angemeldet hat, während es den Verwalter noch nicht gab. */
    for (const bindung of bindungenRef.current) verwalter.trackedOrigins.add(bindung);

    rueckgaengigRef.current = verwalter;
    setzeRueckgaengig(verwalter);

    const standMelden = () => {
      setzeKannZurueck(verwalter.canUndo());
      setzeKannVor(verwalter.canRedo());
    };
    verwalter.on("stack-item-added", standMelden);
    verwalter.on("stack-item-popped", standMelden);
    verwalter.on("stack-cleared", standMelden);

    /* Die gewohnten Tastenkürzel. Ohne die sucht niemand einen Knopf. */
    const beiTaste = (e) => {
      const befehl = e.metaKey || e.ctrlKey;
      if (!befehl || e.key.toLowerCase() !== "z") return;
      e.preventDefault();
      if (e.shiftKey) verwalter.redo();
      else verwalter.undo();
    };
    document.addEventListener("keydown", beiTaste);

    return () => {
      document.removeEventListener("keydown", beiTaste);
      verwalter.destroy();
      rueckgaengigRef.current = null;
      setzeRueckgaengig(null);
    };
  }, [bereit, doc]);

  /*
    Der Vorführmodus wird von genau einer Stelle beendet — sonst risse die
    Vollbild-Wirkung bei jedem Neuzeichnen ab und forderte das Vollbild
    gleich wieder an. Das sähe aus wie ein Flackern und wäre eins.
  */
  const beendeVorfuehrung = useCallback(() => setzeFuehrtVor(false), []);

  /*
    Drucken.

    Der Druckstapel entsteht erst auf Knopfdruck und verschwindet danach
    wieder — dauerhaft im Baum wären es bei zwanzig Folien sechzig
    Editoren, die niemand sieht.

    Die kurze Pause davor ist nötig: `window.print()` friert die Seite in
    dem Zustand ein, in dem sie gerade ist. Ohne sie ginge der Stapel leer
    in den Drucker, weil React ihn noch gar nicht gezeichnet hätte.
  */
  useEffect(() => {
    if (!druckt) return undefined;

    const uhr = setTimeout(() => window.print(), 400);
    const fertig = () => setzeDruckt(false);
    window.addEventListener("afterprint", fertig);

    return () => {
      clearTimeout(uhr);
      window.removeEventListener("afterprint", fertig);
    };
  }, [druckt]);

  /* eslint-disable-next-line no-unused-vars */
  const _takt = takt;
  const folien = bereit ? sortierteFolien(doc) : [];
  const stelle = Math.min(aktuelle, Math.max(0, folien.length - 1));
  const folie = folien[stelle] ? folien[stelle].folie : null;

  /* ---------- Kommentare an den Kästen ---------- */

  /*
    Kommentare hängen am KASTEN, nicht an der Folie.

    In `notizen` nimmt `datei` die Dokumentkennung und `absatz` die Kennung
    des Kastens — das Feld ist freier Text, es braucht also keine neue
    Spalte. Die genaue Stelle im Text steht als Verankerung von Yjs daneben,
    genau wie im Textdokument: Wer davor schreibt, verschiebt den Kommentar
    mit, ohne dass jemand nachrechnen muss.

    Gezeigt werden nur die der aktuellen Folie. Alle auf einmal wären eine
    Liste ohne Bezug — man sähe zwanzig Bemerkungen und wüsste zu keiner,
    wohin sie gehört.
  */
  const kastenIds = useMemo(() => {
    if (!folie) return [];
    return (folie.get("kaesten") || { toArray: () => [] })
      .toArray()
      .map((k) => k.get("id"));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [folie, takt]);

  const markierungen = useMemo(() => {
    if (!bereit) return [];
    return (zustand.annotationen || [])
      .filter(
        (a) =>
          a.dateiId === dokumentId &&
          kastenIds.includes(a.absatzId) &&
          (a.sichtbarkeit === "fraktion" || a.verfasserId === ich.id) &&
          a.notiz &&
          a.notiz.trim()
      )
      .map((a) => ({ ...a, stelle: loeseVerankerung(doc, a.verankerung) }))
      .sort((x, y) => (x.stelle ? x.stelle.start : 1e9) - (y.stelle ? y.stelle.start : 1e9));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [bereit, zustand.annotationen, dokumentId, kastenIds, ich.id, doc, takt]);

  async function notizSichern(text) {
    const stelle = notizEntwurf;
    if (!stelle || !stelle.kasten) return;
    const kastenText = stelle.kasten.get("text");
    if (!kastenText) return;
    try {
      await aktionen.fuegeAnnotationHinzu({
        dateiId: dokumentId,
        absatzId: stelle.kasten.get("id"),
        start: stelle.von,
        ende: stelle.bis,
        zitat: stelle.zitat,
        notiz: text,
        sichtbarkeit: "fraktion",
        farbe: "gelb",
        verankerung: verankere(kastenText, stelle.von, stelle.bis),
      });
      setzeNotizEntwurf(null);
      setzeAuswahl(null);
    } catch (f) {
      setzeFehler(f.message);
    }
  }

  /*
    Vom Kommentar am Rand zurück in den Kasten. Liegt der Kasten auf einer
    anderen Folie, wird zuerst dorthin gewechselt — sonst zeigte der Sprung
    ins Leere.
  */
  function springeZu(notiz) {
    if (!notiz.stelle) return;
    const nr = folien.findIndex(({ folie: f }) =>
      (f.get("kaesten") || { toArray: () => [] })
        .toArray()
        .some((k) => k.get("id") === notiz.absatzId)
    );
    if (nr >= 0 && nr !== stelle) setzeAktuelle(nr);
    setzeGewaehlterKasten(notiz.absatzId);

    /*
      Erst im nächsten Durchlauf auswählen. Liegt der Kasten auf einer
      anderen Folie, gibt es sein Quill in diesem Augenblick noch gar nicht
      — es entsteht erst, wenn React die neue Folie gezeichnet hat.
    */
    setTimeout(() => {
      const quill = quillsRef.current.get(notiz.absatzId);
      if (!quill) return;
      quill.setSelection(notiz.stelle.start, notiz.stelle.ende - notiz.stelle.start, "user");
      quill.focus();
    }, 60);
  }

  function neueFolie() {
    doc.transact(() => folienVon(doc).push([baueFolie(naechsteReihenfolge(doc))]));
    setzeAktuelle(folien.length);
  }

  /* Einen Kasten auf die aktuelle Folie legen und gleich auswählen. */
  function legeAn(kasten) {
    if (!folie) return;
    doc.transact(() => folie.get("kaesten").push([kasten]));
    setzeGewaehlterKasten(kasten.get("id"));
  }

  function neuesTextfeld() {
    /*
      Etwas versetzt zur Mitte, nicht genau darauf: Zwei Textfelder
      hintereinander lägen sonst deckungsgleich übereinander, und das zweite
      sähe aus, als wäre es gar nicht angelegt worden.
    */
    const versatz = (folie.get("kaesten").length % 5) * 3;
    legeAn(
      baueKasten({
        art: "text",
        x: 20 + versatz,
        y: 35 + versatz,
        breite: 45,
        hoehe: 18,
        groesse: 4.5,
      })
    );
  }

  /*
    Ein Bild kommt über die vorhandene Ablage: Es wird als Datei der
    Fraktion hochgeladen und liegt danach auch in „Dateien und Akten“. So
    gibt es keinen zweiten Ort für Bilder — und die Zugriffsregeln, die
    dort gelten, gelten auch hier.
  */
  async function neuesBild(datei) {
    if (!datei || !folie) return;
    setzeFehler("");
    setzeLaedtBild(true);
    try {
      const hochgeladen = await ladeDateiHoch(token, {
        titel: datei.name || "Bild",
        datei,
        fraktionId: konto.fraktion,
        kontoId: konto.id,
        nichtoeffentlich: false,
      });

      const { breite, hoehe } = await masseFuer(datei);

      legeAn(
        baueKasten({
          art: "bild",
          x: 50 - breite / 2,
          y: 50 - hoehe / 2,
          breite,
          hoehe,
          datei: hochgeladen.id,
          sammlung: hochgeladen.collectionId,
          dateiname: hochgeladen.datei,
        })
      );
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLaedtBild(false);
    }
  }

  function entferneFolie(nr) {
    /*
      Die letzte Folie bleibt. Eine Präsentation ohne Folie hätte nichts,
      worin man tippen könnte — man käme nicht wieder hinein.
    */
    if (folien.length <= 1) return;
    const ziel = folien[nr];
    if (!ziel) return;
    const imFeld = folienVon(doc).toArray().indexOf(ziel.folie);
    if (imFeld === -1) return;
    doc.transact(() => folienVon(doc).delete(imFeld, 1));
    setzeAktuelle(Math.max(0, nr - 1));
  }

  function schiebe(nr, richtung) {
    const ziel = folien[nr];
    if (!ziel) return;
    verschiebeFolie(doc, ziel.folie.get("id"), nr + richtung);
    setzeAktuelle(Math.max(0, Math.min(nr + richtung, folien.length - 1)));
  }

  async function sichereTitel() {
    const neu = titel.trim();
    if (!neu || !satz || neu === satz.titel) {
      setzeTitel(satz ? satz.titel : "");
      return;
    }
    try {
      setzeSatz(await benenneDokument(token, dokumentId, neu));
    } catch (f) {
      setzeFehler(f.message);
      setzeTitel(satz.titel);
    }
  }

  if (laedt) {
    return <p className="leise" style={{ marginTop: "var(--luft-5)" }}>Wird geöffnet …</p>;
  }

  if (fehler && !satz) {
    return (
      <div className="karte" style={{ marginTop: "var(--luft-5)" }}>
        <p className="tor__fehler">
          <Warnung groesse={18} />
          <span>{fehler}</span>
        </p>
        <button
          className="knopf knopf--zurueckhaltend"
          style={{ marginTop: "var(--luft-4)" }}
          onClick={() => oeffne("office", {})}
        >
          Zurück zu Office
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="detailkopf nicht-drucken">
        <p className="detailkopf__vorspann">
          {fraktion ? `${fraktion.name} · ${fraktion.kommune}` : "Fraktion"}
        </p>

        <div className="dokumentkopf">
          <input
            className="dokumenttitel"
            value={titel}
            onChange={(e) => setzeTitel(e.target.value)}
            onBlur={sichereTitel}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            aria-label="Titel der Präsentation"
          />

          <div className="dokumentkopf__leute">
            {andere.map((p) => (
              <span
                key={p.kontoId}
                className="leutekreis"
                style={{ background: p.farbe || farbeFuer(p.kontoId) }}
                title={`${p.name} ist in der Präsentation`}
              >
                {kuerzel(p.name)}
              </span>
            ))}
          </div>
        </div>

        {fehler && (
          <p className="klein" style={{ color: "var(--ampel-nein)" }} role="alert">
            {fehler}
          </p>
        )}
      </div>

      {/*
        Die Leiste steht über allem — nicht an der Folie und nicht am
        Kasten. Dieselbe Stelle wie im Textdokument, damit man sie nicht
        zweimal suchen lernen muss.
      */}
      <Folienleiste
        quill={schreibstelle}
        rueckgaengig={rueckgaengig}
        kannZurueck={kannZurueck}
        kannVor={kannVor}
        laedtBild={laedtBild}
        aufTextfeld={neuesTextfeld}
        aufBild={() => bildWaehler.current && bildWaehler.current.click()}
        aufVorfuehren={() => setzeFuehrtVor(true)}
        aufDrucken={() => setzeDruckt(true)}
        kannKommentieren={!!auswahl && !notizEntwurf}
        aufKommentar={() => setzeNotizEntwurf(auswahl)}
      />

      {/*
        Der Dateiwähler des Browsers lässt sich nicht gestalten, also bleibt
        er unsichtbar und wird vom Knopf in der Leiste ausgelöst — derselbe
        Weg wie in „Dateien und Akten“.
      */}
      <input
        ref={bildWaehler}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const datei = e.target.files && e.target.files[0];
          e.target.value = "";
          neuesBild(datei);
        }}
      />

      <div className="foliensaal">
        {/* ---------- Der Streifen ---------- */}

        <aside className="folienstreifen nicht-drucken">
          {folien.map(({ folie: f }, nr) => (
            <div key={f.get("id")} className="folienstreifen__eintrag">
              <button
                className={`folienstreifen__blatt ${nr === stelle ? "folienstreifen__blatt--aktiv" : ""}`}
                onClick={() => setzeAktuelle(nr)}
                aria-label={`Folie ${nr + 1}`}
                aria-current={nr === stelle ? "true" : undefined}
              >
                <span className="folienstreifen__nummer">{nr + 1}</span>
                <span className="folienstreifen__vorschau">
                  <Folienbuehne folie={f} zugang={zugang} klein />
                </span>
              </button>

              {/*
                Umsortieren über Knöpfe, nicht über Ziehen. Das Ziehen kommt
                im nächsten Schritt dazu — der Knopfweg bleibt daneben
                bestehen, wie beim Aufgabenbrett.
              */}
              <div className="folienstreifen__knoepfe">
                <button
                  className="knopf knopf--schlicht"
                  onClick={() => schiebe(nr, -1)}
                  disabled={nr === 0}
                  aria-label={`Folie ${nr + 1} nach oben`}
                  title="Nach oben"
                >
                  ↑
                </button>
                <button
                  className="knopf knopf--schlicht"
                  onClick={() => schiebe(nr, 1)}
                  disabled={nr === folien.length - 1}
                  aria-label={`Folie ${nr + 1} nach unten`}
                  title="Nach unten"
                >
                  ↓
                </button>
                <button
                  className="knopf knopf--schlicht"
                  onClick={() => entferneFolie(nr)}
                  disabled={folien.length <= 1}
                  aria-label={`Folie ${nr + 1} entfernen`}
                  title="Entfernen"
                >
                  <Kreuz groesse={14} />
                </button>
              </div>
            </div>
          ))}

          <button className="brett__plus" onClick={neueFolie}>
            + Folie
          </button>
        </aside>

        {/* ---------- Die Bühne ---------- */}

        <div className="buehne">
          <Folienbuehne
            folie={folie}
            gewaehlt={gewaehlterKasten}
            aufWahl={setzeGewaehlterKasten}
            aufSchreibstelle={merkeSchreibstelle}
            aufBindung={merkeBindung}
            aufQuill={merkeQuill}
            zugang={zugang}
          />
          <p className="klein leise buehne__fuss nicht-drucken">
            Folie {stelle + 1} von {folien.length}
            {" · "}
            <button
              className="knopf knopf--schlicht"
              onClick={() => oeffne("office", {})}
              style={{ padding: 0 }}
            >
              Zurück zu Office
              <PfeilRechts groesse={14} />
            </button>
          </p>
        </div>

        {/*
          Die Kommentare der aktuellen Folie. Dieselbe Spalte wie am
          Textdokument — es ist wörtlich derselbe Baustein.
        */}
        <Randnotizen
          markierungen={markierungen}
          aufSpringen={springeZu}
          aufBearbeiten={setzeNotizBearbeiten}
        />
      </div>

      {/*
        Der Druckstapel. Auf dem Bildschirm unsichtbar, im Papier-Fall eine
        Folie je Seite.
      */}
      {druckt && (
        <div className="foliendruck">
          {folien.map(({ folie: f }) => (
            <div className="foliendruck__blatt" key={f.get("id")}>
              <Folienbuehne folie={f} zugang={zugang} nurLesen />
            </div>
          ))}
        </div>
      )}

      {notizEntwurf && (
        <Notizfeld
          zitat={notizEntwurf.zitat}
          aufSichern={notizSichern}
          aufAbbrechen={() => {
            setzeNotizEntwurf(null);
            setzeAuswahl(null);
          }}
        />
      )}

      {notizBearbeiten && (
        <Notizfeld
          zitat={notizBearbeiten.zitat}
          wert={notizBearbeiten.notiz}
          aufSichern={async (text) => {
            await aktionen.setzeAnnotationNotiz(notizBearbeiten.id, text);
            setzeNotizBearbeiten(null);
          }}
          aufEntfernen={async () => {
            await aktionen.entferneAnnotation(notizBearbeiten.id);
            setzeNotizBearbeiten(null);
          }}
          aufAbbrechen={() => setzeNotizBearbeiten(null)}
        />
      )}

      {fuehrtVor && folien.length > 0 && (
        <Vorfuehrung
          folien={folien}
          start={stelle}
          zugang={zugang}
          aufEnde={beendeVorfuehrung}
        />
      )}
    </>
  );
}

/*
  Wie groß der Kasten für ein Bild sein soll — in Prozent der Folie.

  Ein fester Kasten würde jedes Bild entweder stauchen oder mit leeren
  Rändern umgeben. Deshalb wird das Seitenverhältnis aus der Datei gelesen
  und der Kasten danach gerechnet. Dass die Folie 16:9 ist, muss dabei
  hinein: Ein Prozent Breite ist auf ihr fast doppelt so lang wie ein
  Prozent Höhe.

  Lässt sich die Datei nicht lesen — ein beschädigtes Bild, ein Format, das
  der Browser nicht kennt —, kommt ein maßvolles Rechteck zurück. Ein
  Fehler an dieser Stelle darf das Einfügen nicht verhindern; die Größe
  lässt sich von Hand ändern.
*/
async function masseFuer(datei) {
  const standard = { breite: 45, hoehe: 45 };
  try {
    const verhaeltnis = await new Promise((fertig, daneben) => {
      const bild = new Image();
      const adresse = URL.createObjectURL(datei);
      bild.onload = () => {
        URL.revokeObjectURL(adresse);
        fertig(bild.naturalWidth / bild.naturalHeight);
      };
      bild.onerror = () => {
        URL.revokeObjectURL(adresse);
        daneben(new Error("nicht lesbar"));
      };
      bild.src = adresse;
    });

    if (!Number.isFinite(verhaeltnis) || verhaeltnis <= 0) return standard;

    let breite = 45;
    let hoehe = (breite * SEITENVERHAELTNIS) / verhaeltnis;

    /* Zu hoch für die Folie? Dann über die Höhe rechnen. */
    if (hoehe > 70) {
      hoehe = 70;
      breite = (hoehe * verhaeltnis) / SEITENVERHAELTNIS;
    }

    return { breite: Math.round(breite), hoehe: Math.round(hoehe) };
  } catch {
    return standard;
  }
}

/* „Renate Buschmann“ → „RB“ */
function kuerzel(name) {
  return String(name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((teil) => teil.charAt(0).toUpperCase())
    .join("");
}
